/// <reference types="vite/client" />
/**
 * Runtime environment detection and configuration.
 * Determines environment and API targets based on origin URL at runtime.
 */

export type RuntimeEnv = 'production' | 'staging' | 'development' | 'preview' | 'local'

export type RuntimeConfig = {
  env: RuntimeEnv
  appOrigin: string
  apiBaseUrl: string
  gwReactBasePath: '/react'
}

const DEV_API = 'https://api.dev.gtowizard.com'
const PROD_API = 'https://api.gtowizard.com'

// app-dr.gtowiz.com is a prod-equivalent disaster-recovery clone of the app
// (app.gtowizard.com), classified `staging` and pointed at its own DR backend.
const GTOWIZ_HOST = 'gtowiz.com'
const GTOWIZ_API = 'https://api.dev.dr.gtowiz.com'

// Matches the gtowiz.com apex and every subdomain: the whole gtowiz.com zone is
// deliberately one prod-equivalent staging env (not only app-dr), so any host on
// it inherits the DR backend and prod-equivalent behavior by design.
function isGtowizHost(hostname: string): boolean {
  return hostname === GTOWIZ_HOST || hostname.endsWith(`.${GTOWIZ_HOST}`)
}

// ENUMERATED rather than matched apex-and-subdomains like isGtowizHost, because the two
// zones are opposites. gtowiz.com is entirely prod-equivalent, so a wildcard is right
// there. pokerarena.com is a domain-HOLD zone: its apex A record is 192.0.2.1
// (TEST-NET-1), `www` is a CNAME to the apex, and a zone ruleset 301s both to
// app.gtowizard.com/play. `new` is the zone's only Pages custom domain. A wildcard here
// would classify a parked redirect host as production; enumeration lets it fall through
// to `development`.
//
// `poker-arena-web.pages.dev` is the Pages project's apex alias and serves the SAME
// production-branch deployment as `new.pokerarena.com`. Enumerating the bare host is
// load-bearing: classifyEnv consults this array before the `.pages.dev` suffix test, so
// the alias resolves `production` while the labelled hosts beneath it
// (`<slug>.poker-arena-web.pages.dev`) keep resolving `preview`.
//
// The per-deployment `<hash>.<project>.pages.dev` alias is deliberately left `preview` —
// unenumerable, and uniformly dev-tier, so it exposes no production data.
//
// `new.` is explicitly a pre-GA name — revisit this list when the app moves to the apex,
// at which point the apex and `www` flip together, since one redirect rule covers both.
// apps/poker-wizard-web/src/utils/standaloneHost.ts keeps a DIFFERENT list for a different
// question (which hosts serve the standalone bundle, including the redirect sources);
// the terraform zone definition is authoritative for both.
const POKERARENA_PRODUCTION_HOSTS = ['new.pokerarena.com', 'poker-arena-web.pages.dev']

// Per-PR preview-backend override. `?api=<n>` on a non-production host points the
// API at the gto-wizard backend preview `pr-<n>-api.preview.dev.gtowizard.com`,
// so a standard preview build can be pointed at any backend without a rebuild.
const PREVIEW_API_PARAM = 'api'
const PREVIEW_API_STORAGE_KEY = 'gtow_preview_api'

/**
 * Build the preview-backend base URL for a validated PR number. Kept as the one
 * place the `pr-<n>-api…` host is assembled so the digit constraint and the host
 * shape never drift apart.
 */
function previewApiBaseUrl(pr: string): string {
  return `https://pr-${pr}-api.preview.dev.gtowizard.com`
}

/**
 * Short, at-a-glance tag for a resolved API base URL — the distinguishing token,
 * not the full host. Powers the dev-only preview-backend indicator's collapsed
 * badge (`dev`, `pr-2390`, a per-team dev slug, `proxy`, …); the full URL is
 * shown when the badge is expanded.
 */
export function backendShortTag(apiBaseUrl: string): string {
  // Empty base URL = VITE_PROXY_URL proxy mode (requests go through the dev server).
  if (apiBaseUrl === '') {
    return 'proxy'
  }

  let hostname: string
  try {
    hostname = new URL(apiBaseUrl).hostname
  } catch {
    return apiBaseUrl
  }

  // pr-<n>-api.preview.dev.gtowizard.com → `pr-<n>`
  const pr = /^pr-(\d+)-api\./.exec(hostname)
  if (pr !== null) {
    return `pr-${pr[1] ?? ''}`
  }
  // api.dev.gtowizard.com → `dev`; api.<team>.dev.gtowizard.com → `<team>`
  const dev = /^api\.(?:([a-z0-9-]+)\.)?dev\.gtowizard\.com$/.exec(hostname)
  if (dev !== null) {
    return dev[1] ?? 'dev'
  }
  if (hostname === PROD_API.replace('https://', '')) {
    return 'prod'
  }

  return hostname
}

/**
 * Read the sticky preview-backend PR number from localStorage, re-validating it.
 *
 * Read-only: never writes. Returns the numeric PR string when a valid override
 * is persisted, else null. Re-validates on read so a poisoned/stale entry can't
 * smuggle a non-numeric host. Best-effort — localStorage access can throw in
 * privacy modes / SSR, in which case there is no sticky override.
 */
export function getActivePreviewBackendPr(): string | null {
  try {
    const stored = localStorage.getItem(PREVIEW_API_STORAGE_KEY)

    return stored !== null && /^\d+$/.test(stored) ? stored : null
  } catch {
    return null
  }
}

/**
 * Clear the sticky preview-backend override.
 *
 * Removes the persisted PR number so subsequent loads fall back to hostname
 * detection. The current URL may still carry `?api=<n>` — callers that want an
 * immediate reset should also strip that param before reloading, otherwise the
 * next {@link getRuntimeConfig} re-persists it. Best-effort (privacy modes can
 * throw); a failure is non-fatal since the override simply stays until the store
 * becomes writable.
 */
export function clearPreviewBackendOverride(): void {
  try {
    localStorage.removeItem(PREVIEW_API_STORAGE_KEY)
  } catch {
    // localStorage unavailable — nothing was persisted to clear.
  }
}

/**
 * Resolve a per-PR preview-backend override from `?api=<n>`.
 *
 * `<n>` is constrained to digits, so the only host this can ever produce is
 * `pr-<digits>-api.preview.dev.gtowizard.com` — never an arbitrary origin. An
 * explicit `?api=<n>` wins and is persisted to localStorage so it survives
 * client-side navigation, reloads (which drop the query string), and full
 * browser restarts (OPS-861 — the target is meant to stick across sessions until
 * explicitly reset); `?api=` (empty / non-numeric) clears the sticky value.
 * Returns null when no override is active. localStorage access is best-effort
 * (privacy modes can throw).
 */
function resolvePreviewBackendOverride(loc: Pick<Location, 'search'>): string | null {
  const param = new URLSearchParams(loc.search).get(PREVIEW_API_PARAM)
  let pr: string | null = null

  if (param === null) {
    // No explicit ?api in the URL — reuse a sticky value persisted by an earlier ?api.
    pr = getActivePreviewBackendPr()
  } else {
    // Explicit ?api wins; persist a numeric value, or clear the sticky one on ?api= / garbage.
    pr = /^\d+$/.test(param) ? param : null
    try {
      if (pr === null) {
        localStorage.removeItem(PREVIEW_API_STORAGE_KEY)
      } else {
        localStorage.setItem(PREVIEW_API_STORAGE_KEY, pr)
      }
    } catch {
      // localStorage unavailable — the URL param still applies for this load.
    }
  }

  return pr === null ? null : previewApiBaseUrl(pr)
}

function deriveDevApiFromHostname(hostname: string): string | null {
  if (!hostname.endsWith('.dev.gtowizard.com')) {
    return null
  }

  // Map FE host → BE host by swapping the leading subdomain to `api.`:
  // - app.dev.gtowizard.com         -> api.dev.gtowizard.com
  // - app.<team>.dev.gtowizard.com  -> api.<team>.dev.gtowizard.com
  // - admin.dev.gtowizard.com       -> api.dev.gtowizard.com
  // - admin.<team>.dev.gtowizard.com -> api.<team>.dev.gtowizard.com
  // Only FE prefixes we ship are recognised — anything else returns null and
  // the caller falls back to the shared dev API.
  for (const prefix of ['app.', 'admin.'] as const) {
    if (hostname.startsWith(prefix)) {
      const suffix = hostname.slice(prefix.length)

      return `https://api.${suffix}`
    }
  }

  return null
}

/**
 * Map an admin-web host to its paired customer-app host, for the OAuth
 * identity-provider redirect:
 * - admin.dev.gtowizard.com        -> app.dev.gtowizard.com
 * - admin.<team>.dev.gtowizard.com -> app.<team>.dev.gtowizard.com
 *
 * Returns null for hosts with no known team suffix (localhost, *.pages.dev,
 * production) — callers fall back to their existing shared-dev/prod default.
 */
export function deriveDevAppFromHostname(hostname: string): string | null {
  if (!hostname.endsWith('.dev.gtowizard.com') || !hostname.startsWith('admin.')) {
    return null
  }

  const suffix = hostname.slice('admin.'.length)

  return `https://app.${suffix}`
}

export function deriveAdminFromAppHostname(hostname: string): string | null {
  if (!hostname.endsWith('.dev.gtowizard.com') || !hostname.startsWith('app.')) {
    return null
  }

  const suffix = hostname.slice('app.'.length)

  return `https://admin.${suffix}`
}

// Fixed backend per environment. The `default` guard enforces exhaustiveness at
// compile time: adding a RuntimeEnv member without a case leaves `env` non-never
// here, so the `never` assignment fails to type-check (the `strict` preset does
// not include noImplicitReturns, so a bare switch would silently return undefined).
function apiBaseUrlForEnv(env: RuntimeEnv, hostname: string): string {
  switch (env) {
    case 'production':
      return PROD_API
    case 'staging':
      return GTOWIZ_API
    case 'development':
      return deriveDevApiFromHostname(hostname) ?? DEV_API
    case 'preview':
    case 'local':
      return DEV_API
    default: {
      const exhaustive: never = env

      return exhaustive
    }
  }
}

/**
 * Classify the runtime environment based on hostname.
 */
function classifyEnv(hostname: string): RuntimeEnv {
  if (
    hostname === 'app.gtowizard.com' ||
    hostname === 'admin.gtowizard.com' ||
    POKERARENA_PRODUCTION_HOSTS.includes(hostname)
  ) {
    return 'production'
  }
  if (isGtowizHost(hostname)) {
    return 'staging'
  }
  if (hostname.endsWith('.pages.dev')) {
    return 'preview'
  }
  if (hostname.endsWith('.dev.gtowizard.com')) {
    return 'development'
  }
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return 'local'
  }

  // Default to development for unknown hostnames
  return 'development'
}

/**
 * Compute runtime configuration from a location object.
 * Can be used with window.location or a mock location for testing.
 */
export function computeRuntimeConfig(
  loc: Pick<Location, 'protocol' | 'host' | 'hostname'>,
): RuntimeConfig {
  const env = classifyEnv(loc.hostname)
  const apiBaseUrl = apiBaseUrlForEnv(env, loc.hostname)

  return {
    env,
    appOrigin: `${loc.protocol}//${loc.host}`,
    apiBaseUrl,
    gwReactBasePath: '/react',
  }
}

/**
 * Get the current runtime configuration.
 * Safe for SSR/tests - returns development defaults when window is undefined.
 *
 * @note For local development, you can override the API URL by setting VITE_PROXY_URL
 * environment variable. When set, the client will use empty string as baseURL to enable
 * proxy mode (requests go through dev server proxy to avoid CORS).
 */
export function getRuntimeConfig(): RuntimeConfig {
  if (typeof window === 'undefined') {
    // Default to dev for build/tests; tests can spy on computeRuntimeConfig
    return {
      env: 'development',
      appOrigin: '',
      apiBaseUrl: DEV_API,
      gwReactBasePath: '/react',
    }
  }

  const config = computeRuntimeConfig(window.location)

  // Per-PR preview-backend override via `?api=<n>` (highest priority on non-prod-like
  // hosts). Lets a reviewer point a standard preview build at any backend preview at
  // runtime — no rebuild — taking priority over VITE_PROXY_URL and hostname detection.
  // Never honoured on production or staging (both pin a fixed, prod-like backend).
  if (config.env !== 'production' && config.env !== 'staging') {
    const previewApiBaseUrl = resolvePreviewBackendOverride(window.location)
    if (previewApiBaseUrl !== null) {
      return {
        ...config,
        apiBaseUrl: previewApiBaseUrl,
      }
    }
  }

  // Allow VITE_PROXY_URL to override the runtime-detected API URL for local development
  // When set, return empty string to signal proxy mode (dev server will proxy to VITE_PROXY_URL)
  if (
    typeof import.meta.env.VITE_PROXY_URL === 'string' &&
    import.meta.env.VITE_PROXY_URL.length > 0
  ) {
    return {
      ...config,
      apiBaseUrl: '', // Empty string signals to use relative URLs (proxied by dev server)
    }
  }

  return config
}

/**
 * Build a runtime-config selector keyed by the classified {@link RuntimeEnv}.
 *
 * Typing `byEnv` as `Record<RuntimeEnv, T>` forces callers to supply a value for
 * every env member — including `staging` (the app-dr DR clone) — so a forgotten
 * env is a compile error, not a silent fallthrough. The returned selector reads
 * the live classification via {@link getRuntimeConfig} on each call, inheriting
 * its SSR-safety (window-undefined → `development`) and the `?api=<n>` / staging
 * handling already implemented above.
 *
 * @example
 *   const getAppConfig = defineRuntimeConfig<AppConfig>({
 *     production: PROD, staging: DEV, development: DEV, preview: DEV, local: DEV,
 *   })
 *   getAppConfig() // → the value for the current runtime env
 */
export function defineRuntimeConfig<T>(byEnv: Record<RuntimeEnv, T>): () => T {
  return () => byEnv[getRuntimeConfig().env]
}
